import { useEffect, useMemo, useRef, useState } from 'react';
import { API, useActivePlayers, useScene } from '@unboared/react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FiSettings } from 'react-icons/fi';
import { IoMdSettings } from 'react-icons/io';
import { FaCheck } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';

import { CODE_CLIENT_ID } from '~/config/endpoints.config';
import { excludeGamesForClient } from '~/config/airports.config';
import { FREEPLAY_GAME_IDS } from '~/config/global.config';
import { useConnectionStatus } from '~/hooks/useConnectionStatus';
import { useLanguageManager } from '~/hooks/useLanguageManager';
import { useSentryContext } from '~/hooks/useSentryContext';
import { useSentrySocketBreadcrumbs } from '~/hooks/useSentrySocketBreadcrumbs';
import { useFreePlayPresence, useFreePlayState } from '~/hooks/freePlay';
import { useGame } from '~/hooks/models/useGame';
import { FP_DEFAULTS, VoteContext } from '~/services/freePlay/types';
import { GamepadSettings } from '~/components/GamepadSettings';
import { InterGameScreen } from '~/components/InterGameScreen';
import { useProfileSettings } from '~/stores/useProfileSettings';
import { Button } from '~/components/ui/button';
import { Modal } from '~/components/design/modal';
import { ReconnectingBanner } from '~/components/ReconnectingBanner';
import {
    FreePlayWaitingForPick,
    GamepadActionSheet,
    GameSelector,
    VoteOverlay,
    VoteResultToast,
} from '~/components/freePlay';
import { PhoneWaitingScreen, useRegiePhone } from '~/components/dualScreen/PhoneWaitingScreen';

import { GameGamepad } from './GamepadRunner';
import { useCurrentGameID } from './useCurrentGameID';
import styles from './GamepadManager.module.css';
import { LoadingPage } from '../LoadingPage';

/** Fetches the proposed game and renders the vote overlay. Mounted only while a vote is active. */
function ActiveVoteOverlay({ vote }: { vote: VoteContext }) {
    const proposedGame = useGame(vote.proposedGameID);
    return <VoteOverlay vote={vote} proposedGame={proposedGame} />;
}

export function GamepadManager() {
    useLanguageManager();
    useSentryContext({ clientID: null, deviceType: 'gamepad' });
    useSentrySocketBreadcrumbs();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    // Catalogue free-play filtré des jeux exclus pour ce client (ex. footboared à Orly).
    // Mémorisé sur clientID : une référence de tableau stable évite de relancer le
    // fetch de useGames à chaque re-render (gotcha refs instables).
    const clientID = searchParams.get(CODE_CLIENT_ID) || undefined;
    const freePlayGameIDs = useMemo(() => excludeGamesForClient(FREEPLAY_GAME_IDS, clientID), [clientID]);
    const { scene } = useScene();
    const gameID = useCurrentGameID();
    const { status, isConnected } = useConnectionStatus();
    const { isProfileSettings, infosSaved, toggleProfileSettings, closeInfosSaved } = useProfileSettings();

    // Free-play state (airport clients). Same shared state on every device; in
    // non-free-play (anim) clients `enabled` stays false and the block below is inert.
    const freePlay = useFreePlayState();
    const regiePhone = useRegiePhone();
    const { activePlayers } = useActivePlayers();
    // Away-aware presence: who is *actually* still here (a player who closed their tab
    // lingers in the raw device list for the whole server grace — minutes/hours).
    const { getPresentGamepadIDs } = useFreePlayPresence(freePlay.enabled);
    const [isActionSheetOpen, setActionSheetOpen] = useState(false);
    const [isReplaceSelectorOpen, setReplaceSelectorOpen] = useState(false);

    // Remember the last game we actually saw, to (a) decide we are "between games"
    // and (b) give the rating screen the game that was just played.
    const lastGameRef = useRef<string | undefined>(undefined);
    if (gameID) lastGameRef.current = gameID;
    // Double écran : dernier jeu que la TV a réellement montré (vue « jeu »), pour la
    // notation de l'écran d'attente. Un jeu juste choisi sous l'accueil ne compte pas.
    const regiePlayedRef = useRef<string | undefined>(undefined);
    if (regiePhone?.view === 'game' && gameID) regiePlayedRef.current = gameID;

    // Settle window after mount: on a fresh (re)connect to an active game, the
    // host's gameID shared property syncs within ~1s (askLocalData + the 200ms poll
    // in useCurrentGameID). If none has arrived after this window, assume there is
    // genuinely no active game (host on the selection menu) — see noActiveGame.
    // Heuristic, not a hard signal: on a very slow reconnect into a LIVE game the
    // waiting screen may flash briefly before gameID lands and the controller
    // mounts. 3s covers the vast majority while keeping the refresh fallback snappy.
    const [settled, setSettled] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setSettled(true), 3000);
        return () => clearTimeout(id);
    }, []);

    // Chooser fallback (free-play idle): normally only the first-connected gamepad picks
    // the first game; the rest wait. But if that player never picks (walked away, locked
    // their phone), everyone else would be stuck on "waiting for the first player" forever.
    // After a grace delay, open the selector to every idle gamepad. The host honours only
    // the first start request (phase flips to 'playing'), so a brief two-pick race is harmless.
    const [chooserTimedOut, setChooserTimedOut] = useState(false);
    useEffect(() => {
        if (!freePlay.enabled || freePlay.phase !== 'idle') {
            setChooserTimedOut(false);
            return undefined;
        }
        const id = setTimeout(() => setChooserTimedOut(true), FP_DEFAULTS.chooserFallbackMs);
        return () => clearTimeout(id);
    }, [freePlay.enabled, freePlay.phase]);

    // No active game → show the inter-game waiting / rating screen instead of a
    // frozen controller or an indefinite loader. Two ways to get here:
    //  - live transition: the host returned to the menu and broadcast gameID=null
    //    after we had a game (lastGameRef set) → react immediately.
    //  - fresh load while the host is already on the menu: e.g. the player refreshes
    //    the phone during the between-games screen. The host is away so gameID never
    //    arrives and lastGameRef is reset by the reload → fall back on the settle
    //    window so we don't sit on the loader forever.
    // Can't false-positive mid-game: gameID is always set while a game runs.
    const noActiveGame = !gameID && (!!lastGameRef.current || settled);

    /* Vote-result toast: shown to EVERYONE while the outcome is fresh (accepted/rejected).
       Also read my cooldown ceiling for the replace selector CTA.
       Wrapped in try/catch because API.getDeviceID throws outside a session (gotcha #16). */
    let myCooldownUntil: number | undefined;
    let freshOutcome = null;
    if (freePlay.enabled) {
        if (freePlay.lastVoteOutcome && Date.now() - freePlay.lastVoteOutcome.at < FP_DEFAULTS.voteResultMs) {
            freshOutcome = freePlay.lastVoteOutcome;
        }
        let myDeviceID: string | null = null;
        try {
            myDeviceID = API.getDeviceID();
        } catch {
            /* no active session — leave myDeviceID null */
        }
        if (myDeviceID) {
            const cd = freePlay.initiatorCooldowns?.[myDeviceID];
            if (cd && cd > Date.now()) myCooldownUntil = cd;
        }
    }

    if (!isConnected) {
        return <LoadingPage>{t('gamepad.badInternet')}</LoadingPage>;
    }

    // FREE-PLAY (airport): players pick / vote on the game. Takes over the whole
    // gamepad UI; the active game still flows through the shared `gameID` property
    // (driven host-side by the orchestrator), so the generic game-follow pipeline
    // does the iframe switching — this block only adds the selection / vote UI.
    if (freePlay.enabled) {
        if (!scene) {
            return <LoadingPage>{t('gamepad.loadingParams')}</LoadingPage>;
        }
        if (freePlay.phase === 'idle') {
            // Only the first-connected gamepad (lowest deviceID) that is still present
            // picks the first game; the others wait. Avoids several phones choosing at
            // once (confusing). Once a game is picked, phase → 'playing' and everyone
            // follows. Presence-based so that if the chooser leaves (closes their tab),
            // the next player automatically takes over instead of the lobby freezing.
            const chooserID = [...getPresentGamepadIDs()].sort((a, b) => Number(a) - Number(b))[0];
            let myID: string | null = null;
            try {
                myID = API.getDeviceID();
            } catch {
                /* no active session yet — treat as non-chooser until it syncs */
            }
            if ((myID && chooserID && myID === chooserID) || chooserTimedOut) {
                return <GameSelector mode="start" gameIDs={freePlayGameIDs} />;
            }
            return <FreePlayWaitingForPick />;
        }
        const effectiveGameID = freePlay.currentGameID ?? undefined;
        if (!effectiveGameID) {
            return <LoadingPage>{t('gamepad.loadingParams')}</LoadingPage>;
        }
        const showChangeGame = freePlay.phase === 'playing';

        return (
            <>
                {status === 'reconnecting' && <ReconnectingBanner />}
                <div className="topRight flexRow">
                    <Button
                        preset="secondary"
                        className="color-bg"
                        icon={<IoMdSettings />}
                        onClick={() => setActionSheetOpen(true)}
                    />
                </div>
                {isActionSheetOpen && (
                    <GamepadActionSheet
                        showChangeGame={showChangeGame}
                        onChangeGame={() => {
                            setActionSheetOpen(false);
                            setReplaceSelectorOpen(true);
                        }}
                        onProfile={() => {
                            setActionSheetOpen(false);
                            toggleProfileSettings();
                        }}
                        onClose={() => setActionSheetOpen(false)}
                    />
                )}
                {isReplaceSelectorOpen && (
                    <GameSelector
                        mode="replace"
                        gameIDs={freePlayGameIDs}
                        currentGameID={effectiveGameID}
                        eligibleCount={activePlayers.length}
                        cooldownUntil={myCooldownUntil}
                        onSubmitted={() => setReplaceSelectorOpen(false)}
                        onClose={() => setReplaceSelectorOpen(false)}
                    />
                )}
                {freePlay.phase === 'voting' && freePlay.vote && <ActiveVoteOverlay vote={freePlay.vote} />}
                {freshOutcome && <VoteResultToast outcome={freshOutcome} />}
                {isProfileSettings && <GamepadSettings />}
                {infosSaved && (
                    <Modal>
                        <FaCheck />
                        <p>{t('gamepad.infosSaved')}</p>
                        <IoClose onClick={closeInfosSaved} />
                    </Modal>
                )}
                <GameGamepad gameID={effectiveGameID} />
            </>
        );
    }

    // ANIM mode (default): the host (animator) drives the active game.
    // Double écran : pendant l'accueil ou un message sur la TV, écran d'attente par-dessus
    // la manette (qui reste montée dessous, prête au lancement). L'écran d'attente reste
    // lui aussi monté, au même endroit de l'arbre, caché pendant le jeu ou la modification
    // du profil : une notation commencée n'est pas perdue (cf GameRating).
    const regieWaiting = !!regiePhone && regiePhone.view !== 'game' && !isProfileSettings;
    const regieWait = regiePhone ? (
        <PhoneWaitingScreen
            state={regiePhone}
            clientID={clientID}
            ratedGameID={regiePlayedRef.current}
            hidden={isProfileSettings}
            onEditProfile={toggleProfileSettings}
        />
    ) : null;

    if (noActiveGame) {
        return <InterGameScreen lastGameID={lastGameRef.current} clientID={clientID} />;
    }

    const ready = !!scene && !!gameID;
    if (!ready && !regiePhone) {
        return <LoadingPage>{t('gamepad.loadingParams')}</LoadingPage>;
    }

    return (
        <>
            {status === 'reconnecting' && <ReconnectingBanner />}
            {regieWait}
            {!ready && !regieWaiting && !isProfileSettings && <LoadingPage>{t('gamepad.loadingParams')}</LoadingPage>}
            {ready && (
                <button
                    type="button"
                    className={styles.settingsButton}
                    onClick={toggleProfileSettings}
                    aria-label={t('gamepad.settings.title')}
                >
                    <FiSettings />
                </button>
            )}
            {isProfileSettings && <GamepadSettings />}
            {infosSaved && (
                <Modal>
                    <FaCheck />
                    <p>{t('gamepad.infosSaved')}</p>
                    <IoClose onClick={closeInfosSaved} />
                </Modal>
            )}
            {ready && <GameGamepad gameID={gameID} />}
        </>
    );
}
